import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Dumbbell } from 'lucide-react';

import { BodyFigure } from './BodyFigure';
import { isDrawn } from './bodyShapes';
import { joinList, stepsOf } from '../../utils/exerciseLibrary';

/**
 * One exercise of the library: the video (its picture until it plays, or if it fails), the name and main muscle, a
 * small body marking what it works beside the muscles and the equipment, and the steps. onBack (phones, and desktop
 * when the exercise takes the list's place) adds the way back to the list, named after it
 */
export const ExerciseDetail = ({ exercise, isPt, onBack, backRef }) => {
  const { t } = useTranslation();
  const ui = (key, options) => t(`pages.ExerciseHub.ui.${key}`, options);
  const [videoFailed, setVideoFailed] = useState(false);
  const attrs = exercise.attributes;
  const title = isPt && attrs.titlePt ? attrs.titlePt : attrs.title;
  const steps = stepsOf(attrs, isPt);
  const muscle = (key) => t(`exerciseFilters.muscleGroups.${key}`);
  const others = (attrs.otherMuscles || []).filter((key) => key && key !== attrs.muscleGroup);
  const othersText = joinList(
    others.map((key, index) => (index ? muscle(key).toLowerCase() : muscle(key))),
    ui('and')
  );
  const isFullBody = attrs.muscleGroup === 'full_body';
  const hasFigure = isFullBody || isDrawn(attrs.muscleGroup);
  const worked = { main: attrs.muscleGroup, others, all: isFullBody };

  return (
    <>
      {onBack && (
        <div className="mt-bib-back">
          <button type="button" ref={backRef} className="mt-bib-back__btn" onClick={onBack}>
            <ArrowLeft aria-hidden="true" />
            {ui('title')}
          </button>
        </div>
      )}
      <div className="mt-bib-media">
        {attrs.videoUrl && !videoFailed ? (
          <video
            key={attrs.videoUrl}
            src={attrs.videoUrl}
            poster={attrs.thumbnailUrl || undefined}
            autoPlay
            loop
            muted
            playsInline
            onError={() => setVideoFailed(true)}
          />
        ) : attrs.thumbnailUrl ? (
          <img src={attrs.thumbnailUrl} alt="" />
        ) : (
          <Dumbbell className="mt-bib-art__none" aria-hidden="true" />
        )}
      </div>

      <div className="mt-bib-dhead">
        <h2 className="mt-bib-dhead__title">{title}</h2>
        {attrs.muscleGroup && <p className="mt-bib-dhead__muscle">{muscle(attrs.muscleGroup)}</p>}
      </div>

      <div className={`mt-bib-works${hasFigure ? '' : ' mt-bib-works--plain'}`}>
        {hasFigure && (
          <div className="mt-bib-works__figure">
            <BodyFigure side="front" worked={worked} />
            <BodyFigure side="back" worked={worked} />
          </div>
        )}
        <dl className="mt-bib-works__list">
          {attrs.muscleGroup && (
            <div>
              <dt>{ui('works')}</dt>
              <dd>
                {hasFigure && <i className="mt-bib-works__dot" aria-hidden="true" />}
                {muscle(attrs.muscleGroup)}
              </dd>
            </div>
          )}
          {others.length > 0 && (
            <div>
              <dt>{ui('alsoWorks')}</dt>
              <dd>
                {hasFigure && <i className="mt-bib-works__dot mt-bib-works__dot--also" aria-hidden="true" />}
                {othersText}
              </dd>
            </div>
          )}
          {attrs.equipmentCategory && (
            <div>
              <dt>{ui('equipment')}</dt>
              <dd>{t(`exerciseFilters.equipmentCategories.${attrs.equipmentCategory}`)}</dd>
            </div>
          )}
        </dl>
      </div>

      <section className="mt-bib-how" aria-labelledby="mt-bib-how-title">
        <h3 id="mt-bib-how-title" className="mt-bib-how__title">
          {ui('instructions')}
          {steps.length > 0 && <span>{ui('stepCount', { count: steps.length })}</span>}
        </h3>
        {steps.length > 0 ? (
          <ol className="mt-bib-steps">
            {steps.map((step, index) => (
              <li key={index}>
                <span aria-hidden="true">{index + 1}</span>
                <p>{step}</p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-bib-how__none">{ui('noSteps')}</p>
        )}
      </section>
    </>
  );
};
